import { useQuery, useMutation, useQueryClient } from "react-query";
import { makeAuthedRequest } from "../utils/makeAuthedRequest";
import { parseApiError } from "../utils/apiError";
import { lockRetryOptions } from "../utils/mutationRetry";
import { useContactId, useContactGate } from "./useContactId";

function normalizeNotePayload(payload, contactId) {
  if (!payload) return { text: "" };

  // Some responses return the note directly.
  if (typeof payload === "object" && !Array.isArray(payload)) {
    if (payload.text != null || payload.note != null) {
      return {
        ...payload,
        text: payload.text ?? payload.note ?? "",
      };
    }

    // Other responses return a map keyed by contactId.
    const byContact = payload[String(contactId ?? "")];
    if (byContact && typeof byContact === "object") {
      return {
        ...byContact,
        text: byContact.text ?? byContact.note ?? "",
      };
    }

    // Fallback to first map entry if contact key is missing.
    const firstEntry = Object.values(payload).find(
      (value) => value && typeof value === "object"
    );
    if (firstEntry) {
      return {
        ...firstEntry,
        text: firstEntry.text ?? firstEntry.note ?? "",
      };
    }
  }

  return { text: "" };
}

export function useNote(stockId) {
  const { contactId, gateOpen, gatePending } = useContactGate();
  const query = useQuery(
    // The response is a per-contactId map (household/IBO can have multiple
    // contacts), so contactId is real cache-key data, not decoration. It
    // resolves asynchronously (starts null), so we gate with enabled below
    // rather than firing early and normalizing against the wrong contact.
    ["note", stockId, contactId],
    async () => {
      const res = await makeAuthedRequest(`/notes/${stockId}`);
      if (!res.ok) throw await parseApiError(res, "Note fetch failed");
      const payload = await res.json();
      return normalizeNotePayload(payload, contactId);
    },
    { enabled: !!stockId && gateOpen }
  );

  // See useStockItems: fold isIdle into isLoading using gatePending verbatim
  // so it can never drift from the enabled gate above.
  return {
    ...query,
    isLoading: query.isLoading || (query.isIdle && !!stockId && gatePending),
  };
}

export function useUpdateNote() {
  const contactId = useContactId();
  const queryClient = useQueryClient();
  return useMutation(
    async ({ stockId, text }) => {
      if (contactId == null) {
        throw new Error("Update note failed: missing contactId");
      }
      const res = await makeAuthedRequest(`/notes/${stockId}`, {
        method: "PUT",
        body: JSON.stringify({ text, contactId }),
      });
      if (!res.ok) throw await parseApiError(res, "Update note failed");
      return res.json();
    },
    {
      // Capture contactId at the same moment mutationFn does (onMutate and
      // mutationFn read the same render's closure synchronously before any
      // await), so onSuccess can't drift to a contactId that changed while
      // the request was in flight - the exact bug useContactGate fixes for
      // queries, but mutations need it threaded through context instead.
      onMutate: () => ({ contactId }),
      onSuccess: (updatedNote, { stockId }, context) => {
        queryClient.setQueryData(
          ["note", stockId, context.contactId],
          normalizeNotePayload(updatedNote, context.contactId)
        );
        queryClient.invalidateQueries(["note", stockId]);
      },
    }
  );
}

export function useLike() {
  const contactId = useContactId();
  const queryClient = useQueryClient();
  return useMutation(
    async (stockId) => {
      const res = await makeAuthedRequest(`/likes/${stockId}`, {
        method: "POST",
        body: JSON.stringify({ contactId }),
      });
      if (!res.ok) throw await parseApiError(res, "Like failed");
      return res.json();
    },
    {
      onSettled: () => {
        // Not returned: awaiting the refetch would delay per-call callbacks.
        queryClient.invalidateQueries("stockItems");
      },
      // ItemIconSection flips the heart icon optimistically before this
      // resolves, so the full-screen loader would be a disruptive no-op.
      // See LoadingIndicator.jsx for the meta.blocking convention.
      meta: { blocking: false },
      ...lockRetryOptions,
    }
  );
}

export function useUnlike() {
  const contactId = useContactId();
  const queryClient = useQueryClient();
  return useMutation(
    async (stockId) => {
      if (contactId == null) {
        throw new Error("Unlike failed: missing contactId");
      }
      const query = new URLSearchParams({ contactId: String(contactId) });
      const res = await makeAuthedRequest(`/likes/${stockId}?${query}`, {
        method: "DELETE",
      });
      if (!res.ok) throw await parseApiError(res, "Unlike failed");
    },
    {
      onSettled: () => {
        queryClient.invalidateQueries("stockItems");
      },
      // See useLike: the heart icon already flips optimistically.
      meta: { blocking: false },
      ...lockRetryOptions,
    }
  );
}

export function useSubscribeNotification() {
  const contactId = useContactId();
  return useMutation(
    async (stockId) => {
      const res = await makeAuthedRequest(`/notifications/${stockId}`, {
        method: "POST",
        body: JSON.stringify({ contactId }),
      });
      if (!res.ok)
        throw await parseApiError(res, "Subscribe notification failed");
      return res.json();
    },
    { meta: { blocking: false } }
  );
}

export function useUnsubscribeNotification() {
  const contactId = useContactId();
  return useMutation(
    async (stockId) => {
      const res = await makeAuthedRequest(`/notifications/${stockId}`, {
        method: "DELETE",
        body: JSON.stringify({ contactId }),
      });
      if (!res.ok)
        throw await parseApiError(res, "Unsubscribe notification failed");
    },
    { meta: { blocking: false } }
  );
}
